import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Phone } from 'lucide-react';
import { useToast } from '../ui/UIContext';
import { SEO } from '../seo/SEO';

export function ContactPage() {
  const { t } = useTranslation();
  const showToast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 400));
    setSubmitting(false);
    setName('');
    setEmail('');
    setMessage('');
    showToast(t('contact.success'), 'success');
  }

  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-20">
      <SEO titleKey="seo.contact.title" descriptionKey="seo.contact.description" />
      <div className="grid grid-cols-12 gap-8 mb-16">
        <div className="col-span-12 md:col-span-3">
          <span className="editorial-number text-5xl block">N°03</span>
          <div className="editorial-label mt-3">— Correspondance</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1 className="font-display text-[clamp(3rem,6vw,5.5rem)] leading-[0.95] tracking-tight">
            {t('contact.title')}
          </h1>
          <p className="mt-6 font-display text-xl italic text-ink-soft max-w-2xl">
            {t('contact.lead')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-12 border-t border-linen pt-12">
        <aside className="col-span-12 md:col-span-4 space-y-8">
          <div>
            <div className="editorial-label text-brass mb-3">{t('contact.email')}</div>
            <a className="font-display text-2xl italic brass-link" href="mailto:hello@handcrafted.local">
              hello@handcrafted.local
            </a>
            <Mail className="w-4 h-4 text-brass mt-3" />
          </div>
          <div>
            <div className="editorial-label text-brass mb-3">{t('contact.phone')}</div>
            <a className="font-display text-2xl italic brass-link" href="tel:+33123456789">
              +33 1 23 45 67 89
            </a>
            <Phone className="w-4 h-4 text-brass mt-3" />
          </div>
          <div>
            <div className="editorial-label text-brass mb-3">{t('contact.address')}</div>
            <p className="font-display text-2xl italic leading-snug">
              14, rue du Tailleur
              <br />
              75001 Paris
            </p>
            <MapPin className="w-4 h-4 text-brass mt-3" />
          </div>
        </aside>

        <form onSubmit={handleSubmit} className="col-span-12 md:col-span-8 space-y-8">
          <div>
            <label htmlFor="contact-name" className="editorial-label block mb-2">
              {t('contact.fields.name')}
            </label>
            <input
              id="contact-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-0 py-3 bg-transparent border-b border-linen focus:border-brass outline-none font-display text-xl transition-colors"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="editorial-label block mb-2">
              {t('contact.fields.email')}
            </label>
            <input
              id="contact-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-0 py-3 bg-transparent border-b border-linen focus:border-brass outline-none font-display text-xl transition-colors"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="editorial-label block mb-2">
              {t('contact.fields.message')}
            </label>
            <textarea
              id="contact-message"
              required
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-0 py-3 bg-transparent border-b border-linen focus:border-brass outline-none font-body text-base resize-none transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex items-center gap-4 bg-ink text-ivory px-8 py-4 hover:bg-brass-dark transition-colors disabled:opacity-50"
          >
            <span className="editorial-label text-ivory">
              {submitting ? t('contact.sending') : t('contact.send')}
            </span>
            <span className="font-display text-xl transition-transform group-hover:translate-x-1">→</span>
          </button>
        </form>
      </div>
    </section>
  );
}
